import { useUser } from '@/hooks/useUser';
import { useFlag } from '@simplywallst/unleash';
import { useCallback, useEffect, useState } from 'react';
import { useRegistrationModal } from '@/context/RegistrationModalContext';
import { useTracking } from 'react-tracking';

export const useLessIntrusiveSignupModal = () => {
  const flag = useFlag('mono-company-report-less-intrusive-signup-modal');
  const dataModalTrigger = 'less-intrusive-signup-modal';
  const variantName = flag.variant.name;
  const [canClose, setCanClose] = useState(true);
  const { trackEvent } = useTracking();
  const { data: user } = useUser();

  const isLessIntrusiveSignupModalEnabled =
    (variantName === 'variant1' || variantName === 'variant2') &&
    !user.loggedIn;

  const { showRegoModal: originalShowRegoModal } = useRegistrationModal();

  const showRegoModal = useCallback(
    ({ canClose: canCloseProp }: { canClose?: boolean } = {}) => {
      if (canCloseProp !== undefined) {
        setCanClose(canCloseProp);
      }

      originalShowRegoModal({
        canClose: canCloseProp !== undefined ? canCloseProp : canClose,
        onClose: () => {
          trackEvent({
            type: 'track',
            subject: 'guestRegistrationModal',
            modifier: 'dismissed',
            action: 'click',
          });
        },
        onRegisterSuccess: () => {
          trackEvent({
            type: 'track',
            subject: 'guestRegistrationModal',
            modifier: 'registration',
            action: 'success',
          });
        },
      });
    },
    [canClose, originalShowRegoModal, trackEvent]
  );

  const preventClickAndShowModal = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement> | MouseEvent) => {
      if (isLessIntrusiveSignupModalEnabled) {
        e.preventDefault();
        e.stopPropagation();
        showRegoModal();
      }
    },
    [isLessIntrusiveSignupModalEnabled, showRegoModal]
  );

  const handleClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const clickableTarget = target.closest('a, button, input');
      const isDropdown = target.closest('button[data-active="true"]');

      const shouldShowModal = clickableTarget && !isDropdown;
      if (shouldShowModal) {
        preventClickAndShowModal(e);
      }
    },
    [preventClickAndShowModal]
  ) as EventListener;

  const bindClickEvent = useCallback(() => {
    const modalTriggers = document.querySelectorAll(
      `[data-modal-trigger=${dataModalTrigger}]`
    );
    modalTriggers.forEach((trigger) => {
      trigger.addEventListener('click', handleClick);
    });
  }, [handleClick, dataModalTrigger]);

  const unbindClickEvent = useCallback(() => {
    const modalTriggers = document.querySelectorAll(
      `[data-modal-trigger=${dataModalTrigger}]`
    );
    modalTriggers.forEach((trigger) => {
      trigger.removeEventListener('click', handleClick);
    });
  }, [handleClick, dataModalTrigger]);

  useEffect(() => {
    return () => {
      unbindClickEvent();
    };
  }, [unbindClickEvent]);

  const registerClickEvent = useCallback(() => {
    if (isLessIntrusiveSignupModalEnabled && !user.loggedIn) {
      bindClickEvent();
      return () => {
        unbindClickEvent();
      };
    }
  }, [
    bindClickEvent,
    isLessIntrusiveSignupModalEnabled,
    unbindClickEvent,
    user.loggedIn,
  ]);

  return {
    isLessIntrusiveSignupModalEnabled,
    variantName,
    dataModalTrigger,
    registerClickEvent,
    showRegoModal,
    preventClickAndShowModal,
    canClose,
  };
};
